// @ts-strict-ignore
import _ from 'lodash';
import moment from 'moment-timezone';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { JOURNAL_PREFIX_PATH } from '@/main/app.constants';
import { logError } from '@/utilities/logger';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { errorToast, warnToast } from '@/utilities/toast.utilities';
import { isForbidden } from '@/utilities/http.utilities';
import { formatTime } from '@/datetime/dateTime.utilities';
import HttpCodes from 'http-status-codes';

import {
  sqStateSynchronizer,
  sqTrendCapsuleSetStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { LINK_TYPE } from '@/annotation/annotation.constants';
import { getWorkstepAction } from '@/worksteps/worksteps.actions';
import { addTrendItem } from '@/trendData/trend.actions';
import { displayRange } from '@/trendData/duration.actions';

/**
 * Creates and executes Seeq journal links.
 */

/**
 * Executes a link action
 *
 * @param {Object} params - an object container
 * @param {String} [params.item] - an item ID
 * @param {String} [params.start] - a start time in UTC milliseconds
 * @param {String} [params.end] - an end time in UTC milliseconds
 * @param {String} [params.workbook] - a workbook ID
 * @param {String} [params.worksheet] - a worksheet ID
 */
export async function executeLinkAction(urlParams) {
  const params = Object.fromEntries(urlParams);
  if (params.item) {
    sqItemsApi
      .getItemAndAllProperties({ id: params.item })
      .then(({ data: item }) => addTrendItem(item))
      .catch((response) => {
        if (isForbidden(response)) {
          const currentUser = sqWorkbenchStore.currentUser;
          const messageParams = {
            userName: currentUser.name,
            userId: currentUser.id,
            itemType: params.type,
            itemId: params.item,
          };
          // show warning notification so the user knows the action is not allowed because of insufficient permissions
          warnToast({
            messageKey: 'JOURNAL.ACCESS_WARN_MESSAGE',
            messageParams,
          });
        } else {
          errorToast({ httpResponseOrError: response });
        }
      });
  }

  if (params.start && params.end) {
    displayRange.updateTimes(+params.start, +params.end);
  }

  const workbookId = sqWorkbenchStore.stateParams.workbookId;
  const worksheetId = sqWorkbenchStore.stateParams.worksheetId;
  if (workbookId && worksheetId && params.workstep) {
    const safeGetWorkstepAction = () =>
      getWorkstepAction(params.workbook, params.worksheet, params.workstep, true).catch((response) => {
        if (_.get(response, 'status') === HttpCodes.NOT_FOUND) {
          warnToast({ messageKey: 'JOURNAL.ENTRY.LINKED_WORKSTEP_GONE' });
        }
      });
    await sqStateSynchronizer.getWorkstepAndRehydrate(safeGetWorkstepAction);
  }
}

/**
 * Creates an item link
 *
 * @param {Object} item - an item object container
 * @param {String} item.id - an ID
 * @param {String} item.name - a name
 * @param {String} item.itemType - an ITEM_TYPES
 * @param {String} [item.assets[0].name] - an optional asset name
 * @returns {String} an item link
 */
export function createItemLink(item) {
  const type = {
    [ITEM_TYPES.SERIES]: LINK_TYPE.SIGNAL,
    [ITEM_TYPES.SCALAR]: LINK_TYPE.SIGNAL,
    [ITEM_TYPES.CAPSULE_SET]: LINK_TYPE.CONDITION,
    [ITEM_TYPES.TABLE]: LINK_TYPE.TABLE,
    [ITEM_TYPES.METRIC]: LINK_TYPE.METRIC,
  }[item.itemType];

  if (_.isNil(type)) {
    logError(`itemType: ${item.itemType} has no matching LINK_TYPE`);
  }

  const name = createItemLinkName(item);

  return !type ? '' : `<a href="${JOURNAL_PREFIX_PATH}?type=${type}&item=${item.id}">${name}</a>`;
}

interface CreateCapsuleLinkParams {
  /** an ID */
  conditionId: string;
  /** UTC Unix start time in milliseconds */
  start: number;
  /** UTC Unix end time in milliseconds */
  end: number;
}

/**
 * Creates a condition link
 *
 * @returns - an object containing the link name and the link
 */
export function createCapsuleLink({ conditionId, start, end }: CreateCapsuleLinkParams): {
  name: string;
  link: string;
} {
  const startLabel = formatTime(start, sqWorksheetStore.timezone);
  const conditionName = sqTrendCapsuleSetStore.findItem(conditionId).name;
  const name = `${conditionName} (${startLabel})`;

  return {
    name,
    link: `<a href="${JOURNAL_PREFIX_PATH}?type=${LINK_TYPE.CAPSULE}&item=${conditionId}&start=${start}&end=${end}">${name}</a>`,
  };
}

type CreateTimeRangeLinkParams = {
  /** a label for time range display (e.g. "Display Range") */
  label: string;
  /** UTC Unix start time in milliseconds */
  start: number;
  /** UTC Unix end time in milliseconds */
  end: number;
};

export function createTimeRangeLink({ label, start, end }: CreateTimeRangeLinkParams) {
  const timeRangeLabel = `${formatTime(start, sqWorksheetStore.timezone)} - ${formatTime(
    end,
    sqWorksheetStore.timezone,
  )}`;
  const name = `${label} (${timeRangeLabel})`;
  return {
    name,
    link: `<a href="${JOURNAL_PREFIX_PATH}?type=${LINK_TYPE.RANGE}&start=${start}&end=${end}">${timeRangeLabel}</a>`,
  };
}

/**
 * Creates a workstep link based on the current workstep
 *
 * @returns {{name: string, link: string}} an object containing the link name and the link
 */
export function createWorkstepLink(stateParams) {
  const name = `${sqWorkbookStore.name} - ${sqWorkbookStore.getWorksheetName(stateParams.worksheetId)} (${formatTime(
    moment.utc().valueOf(),
    sqWorksheetStore.timezone,
  )})`;
  return {
    name,
    link: `<a href="${JOURNAL_PREFIX_PATH}?type=${LINK_TYPE.WORKSTEP}&workbook=${stateParams.workbookId}&worksheet=${stateParams.worksheetId}&workstep=${sqWorkstepsStore.current.id}">${name}</a>`,
  };
}

/**
 * Creates a name for an item link
 *
 * @param {Object} item - an item object container
 * @param {String} item.name - a name
 * @param {String} [item.assets[0].name] - an optional asset name
 * @returns {String} an item link name
 */
export function createItemLinkName(item) {
  return item.name + (item.assets && item.assets.length ? ` (${item.assets[0].name})` : '');
}

/**
 * Replaces the current name of Seeq link with another name
 *
 * @param {String} link - a Seeq link
 * @param {String} name - the new name for the Seeq link
 * @returns {String} a Seeq link with the name replaced
 */
export function replaceLinkName(link, name) {
  return link.replace(/(^<.*?>)(.*)(<.*?>$)/g, `$1${name}$3`);
}
